import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'chart.js/auto';
import { getGrowthDashboard } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DEAL_TYPE_MAP = {
  new_business: { label: 'New Business', className: 'new-biz' },
  recompete: { label: 'Recompete', className: 'recompete' },
  organic_growth: { label: 'Organic Growth', className: 'organic' },
};

function chartColors(theme) {
  const isDark = theme !== 'light';
  return {
    textMuted: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.6)',
    textDim: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.4)',
    gridLine: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)',
  };
}

function formatCurrency(value) {
  if (value === null || value === undefined) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(value);
}

function formatCompactCurrency(value) {
  if (value === null || value === undefined) return '--';
  if (Math.abs(value) >= 1000000) return '$' + (value / 1000000).toFixed(1) + 'M';
  if (Math.abs(value) >= 1000) return '$' + (value / 1000).toFixed(0) + 'K';
  return '$' + value.toLocaleString();
}

function formatMonthLabel(monthStr) {
  if (!monthStr) return '--';
  const [y, m] = monthStr.split('-');
  return MONTHS_SHORT[parseInt(m, 10) - 1] + ' ' + y.slice(2);
}

function pwinClass(pwin) {
  if (pwin >= 0.6) return 'high';
  if (pwin >= 0.3) return 'med';
  return 'low';
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const now = new Date();
  const diff = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
  return diff;
}

function DealTypeBadge({ type }) {
  const info = DEAL_TYPE_MAP[type] || { label: type, className: '' };
  return <span className={`growth-deal-type ${info.className}`}>{info.label}</span>;
}

function PwinBadge({ pwin }) {
  return (
    <span className={`growth-pwin-badge ${pwinClass(pwin)}`}>
      {Math.round(pwin * 100)}%
    </span>
  );
}

function GrowthDashboard({ onNavigate, user }) {
  const { theme } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pipelineTableOpen, setPipelineTableOpen] = useState(false);
  const [dealsTableOpen, setDealsTableOpen] = useState(false);
  const [wonTableOpen, setWonTableOpen] = useState(false);

  const tcvChartRef = useRef(null);
  const tcvChartInstance = useRef(null);
  const bpChartRef = useRef(null);
  const bpChartInstance = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    async function load() {
      try {
        const result = await getGrowthDashboard();
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Monthly TCV Activity Chart
  useEffect(() => {
    if (!tcvChartRef.current || !data?.monthly_tcv_activity) return;
    if (tcvChartInstance.current) tcvChartInstance.current.destroy();

    const colors = chartColors(theme);
    const activity = data.monthly_tcv_activity;
    const labels = activity.map((m) => formatMonthLabel(m.month));

    const ctx = tcvChartRef.current.getContext('2d');
    tcvChartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            type: 'bar',
            label: 'Submitted TCV',
            data: activity.map((m) => m.submitted_tcv),
            backgroundColor: 'rgba(96, 165, 250, 0.8)',
            borderColor: '#60a5fa',
            borderWidth: 1,
            borderRadius: 4,
            order: 2,
          },
          {
            type: 'line',
            label: 'Cumulative Won',
            data: activity.map((m) => m.cumulative_won),
            borderColor: '#d6e200',
            backgroundColor: 'rgba(214, 226, 0, 0.1)',
            borderWidth: 2,
            pointRadius: 3,
            pointBackgroundColor: '#d6e200',
            pointBorderColor: '#d6e200',
            tension: 0.3,
            fill: false,
            order: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            display: true, position: 'bottom',
            labels: {
              color: colors.textMuted,
              font: { family: 'JetBrains Mono', size: 11 },
              boxWidth: 12, padding: 16, usePointStyle: true,
            },
          },
          tooltip: {
            mode: 'index', intersect: false,
            callbacks: {
              label: (ctx) => ` ${ctx.dataset.label}: ${formatCurrency(ctx.raw)}`,
            },
          },
        },
        scales: {
          x: {
            grid: { color: colors.gridLine },
            ticks: { color: colors.textMuted, font: { family: 'JetBrains Mono', size: 10 } },
          },
          y: {
            grid: { color: colors.gridLine },
            ticks: {
              color: colors.textDim,
              font: { family: 'JetBrains Mono', size: 10 },
              callback: (v) => {
                if (v >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M';
                if (v >= 1000) return '$' + (v / 1000).toFixed(0) + 'K';
                return '$' + v;
              },
            },
          },
        },
      },
    });

    return () => { if (tcvChartInstance.current) tcvChartInstance.current.destroy(); };
  }, [data, theme]);

  // B&P Spend Chart
  useEffect(() => {
    if (!bpChartRef.current || !data?.bp_spend?.monthly) return;
    if (bpChartInstance.current) bpChartInstance.current.destroy();

    const colors = chartColors(theme);
    const monthly = data.bp_spend.monthly;
    const labels = monthly.map((m) => formatMonthLabel(m.month));

    const actualColors = monthly.map((m) => {
      if (m.actual === null) return 'rgba(150, 150, 150, 0.15)';
      return m.actual > m.budget ? 'rgba(248, 113, 113, 0.8)' : 'rgba(214, 226, 0, 0.8)';
    });
    const actualBorders = monthly.map((m) => {
      if (m.actual === null) return 'rgba(150, 150, 150, 0.3)';
      return m.actual > m.budget ? '#f87171' : '#d6e200';
    });

    const ctx = bpChartRef.current.getContext('2d');
    bpChartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Budget',
            data: monthly.map((m) => m.budget),
            backgroundColor: 'rgba(150, 150, 150, 0.3)',
            borderColor: 'rgba(150, 150, 150, 0.5)',
            borderWidth: 1,
            borderRadius: 4,
          },
          {
            label: 'Actual',
            data: monthly.map((m) => m.actual),
            backgroundColor: actualColors,
            borderColor: actualBorders,
            borderWidth: 1,
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            display: true, position: 'bottom',
            labels: {
              color: colors.textMuted,
              font: { family: 'JetBrains Mono', size: 11 },
              boxWidth: 12, padding: 16, usePointStyle: true,
            },
          },
          tooltip: {
            mode: 'index', intersect: false,
            callbacks: {
              label: (ctx) => {
                if (ctx.raw === null) return ` ${ctx.dataset.label}: --`;
                return ` ${ctx.dataset.label}: ${formatCurrency(ctx.raw)}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: colors.gridLine },
            ticks: { color: colors.textMuted, font: { family: 'JetBrains Mono', size: 10 } },
          },
          y: {
            grid: { color: colors.gridLine },
            ticks: {
              color: colors.textDim,
              font: { family: 'JetBrains Mono', size: 10 },
              callback: (v) => {
                if (v >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M';
                if (v >= 1000) return '$' + (v / 1000).toFixed(0) + 'K';
                return '$' + v;
              },
            },
          },
        },
      },
    });

    return () => { if (bpChartInstance.current) bpChartInstance.current.destroy(); };
  }, [data, theme]);

  if (loading) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: 'var(--bcore-text-dim)', fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '13px' }}>
        Loading growth data...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ marginBottom: '20px', padding: '16px 20px', background: 'rgba(248, 113, 113, 0.1)', border: '1px solid rgba(248, 113, 113, 0.3)', borderRadius: '8px', color: 'var(--status-critical)', fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }}>
        Unable to load growth data: {error}
      </div>
    );
  }

  if (!data) return null;

  const { kpi_summary, submit_vs_goal, ltm_metrics, pipeline_funnel, kpi_vs_actual, kpi_vs_estimate, submitted_closed_by_type, bp_spend, key_deals, ninety_day_decisions, pipeline_monthly_table, pipeline_deals, won_deals } = data;

  const funnelMax = pipeline_funnel?.qualified?.tcv || 1;

  // Determine closed months from pipeline_monthly_table
  const closedMonthCount = pipeline_monthly_table
    ? pipeline_monthly_table.filter((m) => m.factored_win !== null).length
    : 0;

  return (
    <div>
      {/* Row 1 — KPI Cards */}
      <div className="growth-kpi-row">
        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Qualified Pipeline</span>
            <div className="stat-card-icon blue">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{formatCompactCurrency(kpi_summary?.qualified_pipeline_tcv)}</div>
          <div className="stat-card-sub">{pipeline_funnel?.qualified?.count || 0} opportunities</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Factored Pipeline</span>
            <div className="stat-card-icon" style={{ color: '#a78bfa' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20V10" /><path d="M18 20V4" /><path d="M6 20v-4" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{formatCompactCurrency(kpi_summary?.factored_pipeline_tcv)}</div>
          <div className="stat-card-sub">pWin-weighted</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Submitted TCV</span>
            <div className="stat-card-icon accent">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{formatCompactCurrency(kpi_summary?.submitted_tcv)}</div>
          <div className="stat-card-sub">{data.fiscal_year || 'FY2026'}</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Won TCV</span>
            <div className="stat-card-icon green">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{formatCompactCurrency(kpi_summary?.won_tcv)}</div>
          <div className="stat-card-sub">{data.fiscal_year || 'FY2026'}</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Pending Decision</span>
            <div className="stat-card-icon" style={{ color: '#fbbf24' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{formatCompactCurrency(kpi_summary?.pending_decision_tcv)}</div>
          <div className="stat-card-sub">Awaiting award</div>
        </div>
      </div>

      {/* Row 2 — Submit vs Goal + LTM Metrics */}
      <div className="growth-two-col" style={{ marginBottom: '20px' }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Submit vs Goal</span>
          </div>
          <div className="growth-goal-bar">
            <div className="growth-goal-header">
              <span className="growth-goal-values">
                <strong>{formatCompactCurrency(submit_vs_goal?.submitted_tcv)}</strong> / {formatCompactCurrency(submit_vs_goal?.goal_tcv)}
              </span>
            </div>
            <div className="growth-progress-track">
              <div className="growth-progress-fill" style={{ width: `${Math.min((submit_vs_goal?.percent_complete || 0) * 100, 100)}%` }} />
              <span className="growth-progress-pct">{Math.round((submit_vs_goal?.percent_complete || 0) * 100)}%</span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">LTM Metrics</span>
            <span className="card-action" style={{ cursor: 'default', color: 'var(--bcore-text-dim)' }}>{ltm_metrics?.period}</span>
          </div>
          <div className="growth-ltm-row">
            <div className="growth-ltm-card">
              <div className="growth-ltm-label">Submit</div>
              <div className="growth-ltm-value">{formatCompactCurrency(ltm_metrics?.ltm_submit_tcv)}</div>
            </div>
            <div className="growth-ltm-card">
              <div className="growth-ltm-label">Win</div>
              <div className="growth-ltm-value">{formatCompactCurrency(ltm_metrics?.ltm_win_tcv)}</div>
            </div>
            <div className="growth-ltm-card">
              <div className="growth-ltm-label">Revenue</div>
              <div className="growth-ltm-value">{formatCompactCurrency(ltm_metrics?.ltm_revenue)}</div>
            </div>
            <div className="growth-ltm-card">
              <div className="growth-ltm-label">Book-to-Bill</div>
              <div className="growth-ltm-value">{ltm_metrics?.book_to_bill?.toFixed(2) || '--'}</div>
            </div>
            <div className="growth-ltm-card">
              <div className="growth-ltm-label">Win Rate</div>
              <div className="growth-ltm-value">{ltm_metrics?.win_rate ? Math.round(ltm_metrics.win_rate * 100) + '%' : '--'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 3 — Pipeline Funnel + KPI Progress */}
      <div className="growth-two-col">
        <div className="card">
          <div className="card-header">
            <span className="card-title">Pipeline Conversion Funnel</span>
          </div>
          <div className="growth-funnel">
            {['qualified', 'factored', 'submitted', 'closed', 'won'].map((stage, idx) => {
              const d = pipeline_funnel?.[stage];
              if (!d) return null;
              const widthPct = Math.max((d.tcv / funnelMax) * 100, 8);
              const colors = [
                'rgba(96, 165, 250, 0.5)',
                'rgba(167, 139, 250, 0.6)',
                'rgba(214, 226, 0, 0.7)',
                'rgba(251, 191, 36, 0.7)',
                'rgba(52, 211, 153, 0.85)',
              ];
              return (
                <div key={stage}>
                  <div className="growth-funnel-stage">
                    <div className="growth-funnel-label">{stage.charAt(0).toUpperCase() + stage.slice(1)}</div>
                    <div className="growth-funnel-bar-wrap">
                      <div className="growth-funnel-bar" style={{ width: `${widthPct}%`, background: colors[idx] }}>
                        <span className="growth-funnel-bar-text">{d.count} deals &mdash; {formatCompactCurrency(d.tcv)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card">
          <div className="growth-kpi-progress">
            <div>
              <div className="growth-kpi-section-title">KPI vs Actual</div>
              <div className="growth-kpi-item">
                <div className="growth-kpi-item-header">
                  <span className="growth-kpi-item-label">Submit</span>
                  <span className="growth-kpi-item-values">{formatCompactCurrency(kpi_vs_actual?.submit?.actual)} / {formatCompactCurrency(kpi_vs_actual?.submit?.kpi)} ({Math.round((kpi_vs_actual?.submit?.percent || 0) * 100)}%)</span>
                </div>
                <div className="growth-kpi-mini-track">
                  <div className="growth-kpi-mini-fill accent" style={{ width: `${Math.min((kpi_vs_actual?.submit?.percent || 0) * 100, 100)}%` }} />
                </div>
              </div>
              <div className="growth-kpi-item">
                <div className="growth-kpi-item-header">
                  <span className="growth-kpi-item-label">Won</span>
                  <span className="growth-kpi-item-values">{formatCompactCurrency(kpi_vs_actual?.won?.actual)} / {formatCompactCurrency(kpi_vs_actual?.won?.kpi)} ({Math.round((kpi_vs_actual?.won?.percent || 0) * 100)}%)</span>
                </div>
                <div className="growth-kpi-mini-track">
                  <div className="growth-kpi-mini-fill blue" style={{ width: `${Math.min((kpi_vs_actual?.won?.percent || 0) * 100, 100)}%` }} />
                </div>
              </div>
            </div>

            <div>
              <div className="growth-kpi-section-title">KPI vs Estimate</div>
              <div className="growth-kpi-item">
                <div className="growth-kpi-item-header">
                  <span className="growth-kpi-item-label">Submit</span>
                  <span className="growth-kpi-item-values">
                    {formatCompactCurrency(kpi_vs_estimate?.submit?.estimate)} / {formatCompactCurrency(kpi_vs_estimate?.submit?.kpi)}
                    <span className="growth-win-rate-badge">{Math.round((kpi_vs_estimate?.submit?.win_rate || 0) * 100)}% WR</span>
                  </span>
                </div>
                <div className="growth-kpi-mini-track">
                  <div className="growth-kpi-mini-fill accent" style={{ width: `${Math.min(((kpi_vs_estimate?.submit?.estimate || 0) / (kpi_vs_estimate?.submit?.kpi || 1)) * 100, 100)}%` }} />
                </div>
              </div>
              <div className="growth-kpi-item">
                <div className="growth-kpi-item-header">
                  <span className="growth-kpi-item-label">Won</span>
                  <span className="growth-kpi-item-values">
                    {formatCompactCurrency(kpi_vs_estimate?.won?.estimate)} / {formatCompactCurrency(kpi_vs_estimate?.won?.kpi)}
                    <span className="growth-win-rate-badge">{Math.round((kpi_vs_estimate?.won?.win_rate || 0) * 100)}% WR</span>
                  </span>
                </div>
                <div className="growth-kpi-mini-track">
                  <div className="growth-kpi-mini-fill blue" style={{ width: `${Math.min(((kpi_vs_estimate?.won?.estimate || 0) / (kpi_vs_estimate?.won?.kpi || 1)) * 100, 100)}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 4 — Monthly TCV Activity */}
      <div className="forecast-chart-section" key={`tcv-${theme}`}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Monthly TCV Activity</span>
          </div>
          <div className="chart-container-lg">
            <canvas ref={tcvChartRef}></canvas>
          </div>
        </div>
      </div>

      {/* Row 5 — Submitted & Closed by Type + B&P Spend */}
      <div className="growth-two-col equal">
        <div className="card">
          <div className="card-header">
            <span className="card-title">Submitted & Closed by Type</span>
          </div>
          {submitted_closed_by_type && (
            <table className="growth-type-grid">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Submitted (P)</th>
                  <th>Submitted (S)</th>
                  <th>Won (P)</th>
                  <th>Won (S)</th>
                  <th>Win Rate</th>
                </tr>
              </thead>
              <tbody>
                {['new_business', 'recompete', 'organic_growth'].map((dt) => {
                  const p = submitted_closed_by_type.prime?.[dt] || {};
                  const s = submitted_closed_by_type.sub?.[dt] || {};
                  const totalWon = (p.won || 0) + (s.won || 0);
                  const totalLost = (p.lost || 0) + (s.lost || 0);
                  const totalClosed = totalWon + totalLost;
                  const winRate = totalClosed > 0 ? Math.round((totalWon / totalClosed) * 100) : 0;
                  return (
                    <tr key={dt}>
                      <td><DealTypeBadge type={dt} /></td>
                      <td>{formatCompactCurrency(p.submitted)}</td>
                      <td>{formatCompactCurrency(s.submitted)}</td>
                      <td>{formatCompactCurrency(p.won)}</td>
                      <td>{formatCompactCurrency(s.won)}</td>
                      <td className="win-rate-cell">{winRate}%</td>
                    </tr>
                  );
                })}
                <tr style={{ borderTop: '2px solid var(--bcore-border)', fontWeight: 700 }}>
                  <td>Total</td>
                  <td>{formatCompactCurrency(
                    ['new_business', 'recompete', 'organic_growth'].reduce((sum, dt) => sum + (submitted_closed_by_type.prime?.[dt]?.submitted || 0), 0)
                  )}</td>
                  <td>{formatCompactCurrency(
                    ['new_business', 'recompete', 'organic_growth'].reduce((sum, dt) => sum + (submitted_closed_by_type.sub?.[dt]?.submitted || 0), 0)
                  )}</td>
                  <td>{formatCompactCurrency(
                    ['new_business', 'recompete', 'organic_growth'].reduce((sum, dt) => sum + (submitted_closed_by_type.prime?.[dt]?.won || 0), 0)
                  )}</td>
                  <td>{formatCompactCurrency(
                    ['new_business', 'recompete', 'organic_growth'].reduce((sum, dt) => sum + (submitted_closed_by_type.sub?.[dt]?.won || 0), 0)
                  )}</td>
                  <td className="win-rate-cell">{(() => {
                    const tw = ['new_business', 'recompete', 'organic_growth'].reduce((sum, dt) =>
                      sum + (submitted_closed_by_type.prime?.[dt]?.won || 0) + (submitted_closed_by_type.sub?.[dt]?.won || 0), 0);
                    const tl = ['new_business', 'recompete', 'organic_growth'].reduce((sum, dt) =>
                      sum + (submitted_closed_by_type.prime?.[dt]?.lost || 0) + (submitted_closed_by_type.sub?.[dt]?.lost || 0), 0);
                    const tc = tw + tl;
                    return tc > 0 ? Math.round((tw / tc) * 100) + '%' : '--';
                  })()}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        <div className="card" key={`bp-${theme}`}>
          <div className="card-header">
            <span className="card-title">B&P Spend vs Budget</span>
            {bp_spend && (
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', color: 'var(--bcore-text-dim)' }}>
                YTD: {formatCompactCurrency(bp_spend.ytd_actual)} / {formatCompactCurrency(bp_spend.ytd_budget)}
                <span style={{ color: bp_spend.variance < 0 ? 'var(--status-active)' : 'var(--status-critical)', marginLeft: '8px' }}>
                  {bp_spend.variance < 0 ? '' : '+'}{formatCompactCurrency(bp_spend.variance)}
                </span>
              </span>
            )}
          </div>
          <div className="chart-container-lg" style={{ height: '260px' }}>
            <canvas ref={bpChartRef}></canvas>
          </div>
        </div>
      </div>

      {/* Row 6 — Key Deals + 90-Day Decision Board */}
      <div className="growth-two-col equal">
        <div className="card">
          <div className="card-header">
            <span className="card-title">Key Deals</span>
          </div>
          {key_deals && key_deals.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table className="growth-deal-table">
                <thead>
                  <tr>
                    <th>Deal</th>
                    <th>Customer</th>
                    <th>TCV</th>
                    <th>Type</th>
                    <th>Phase</th>
                    <th>pWin</th>
                    <th>Award</th>
                    <th>Div</th>
                  </tr>
                </thead>
                <tbody>
                  {key_deals.map((deal) => (
                    <tr key={deal.deal_name}>
                      <td className="growth-deal-name">{deal.deal_name}</td>
                      <td>{deal.customer}</td>
                      <td>{formatCompactCurrency(deal.tcv)}</td>
                      <td><DealTypeBadge type={deal.deal_type} /></td>
                      <td>{deal.phase}</td>
                      <td><PwinBadge pwin={deal.pwin} /></td>
                      <td>{deal.expected_award || '--'}</td>
                      <td>{deal.division}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">90-Day Decision Board</span>
          </div>
          {ninety_day_decisions && ninety_day_decisions.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table className="growth-deal-table">
                <thead>
                  <tr>
                    <th>Deal</th>
                    <th>Customer</th>
                    <th>TCV</th>
                    <th>Decision</th>
                    <th>Days</th>
                    <th>pWin</th>
                    <th>Div</th>
                  </tr>
                </thead>
                <tbody>
                  {ninety_day_decisions.map((deal) => {
                    const days = daysUntil(deal.expected_decision);
                    const daysClass = days !== null && days <= 30 ? 'urgent' : days !== null && days <= 60 ? 'soon' : '';
                    return (
                      <tr key={deal.deal_name}>
                        <td className="growth-deal-name">{deal.deal_name}</td>
                        <td>{deal.customer}</td>
                        <td>{formatCompactCurrency(deal.tcv)}</td>
                        <td>{deal.expected_decision || '--'}</td>
                        <td><span className={`growth-decision-days ${daysClass}`}>{days !== null ? `${days}d` : '--'}</span></td>
                        <td><PwinBadge pwin={deal.pwin} /></td>
                        <td>{deal.division}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Row 7 — Pipeline Monthly Table (collapsible) */}
      <div className="forecast-chart-section">
        <div className="card">
          <div className="card-header">
            <span className="card-title">Pipeline Monthly Table</span>
          </div>
          <button
            className={`forecast-expand-bar${pipelineTableOpen ? ' expanded' : ''}`}
            onClick={() => setPipelineTableOpen(!pipelineTableOpen)}
          >
            <svg className="forecast-expand-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
            View Monthly Data
          </button>
          {pipelineTableOpen && pipeline_monthly_table && (
            <div className="forecast-data-table">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    {MONTHS_SHORT.map((m, i) => (
                      <th key={m} style={i < closedMonthCount ? { color: '#d6e200' } : undefined}>{m}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ fontWeight: 600 }}>Unfactored Submit</td>
                    {pipeline_monthly_table.map((m, i) => (
                      <td key={i} style={i < closedMonthCount ? { color: '#d6e200' } : undefined}>
                        {formatCompactCurrency(m.unfactored_submit)}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 600 }}>Factored Win</td>
                    {pipeline_monthly_table.map((m, i) => (
                      <td key={i} style={i < closedMonthCount ? { color: '#d6e200' } : undefined}>
                        {m.factored_win !== null ? formatCompactCurrency(m.factored_win) : '--'}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Row 8 — Pipeline Deals Table (collapsible) */}
      <div className="forecast-chart-section">
        <div className="card">
          <div className="card-header">
            <span className="card-title">Pipeline Deals</span>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', color: 'var(--bcore-text-dim)' }}>
              {pipeline_deals?.length || 0} deals
            </span>
          </div>
          <button
            className={`forecast-expand-bar${dealsTableOpen ? ' expanded' : ''}`}
            onClick={() => setDealsTableOpen(!dealsTableOpen)}
          >
            <svg className="forecast-expand-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
            View All Deals
          </button>
          {dealsTableOpen && pipeline_deals && (
            <div className="forecast-data-table">
              <div style={{ overflowX: 'auto' }}>
                <table className="growth-deal-table">
                  <thead>
                    <tr>
                      <th>Deal</th>
                      <th>Org</th>
                      <th>Capture Mgr</th>
                      <th>TCV</th>
                      <th>Type</th>
                      <th>Prime/Sub</th>
                      <th>Phase</th>
                      <th>pWin</th>
                      <th>Exp. RFP</th>
                      <th>Exp. Award</th>
                      <th>Customer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pipeline_deals.map((deal) => (
                      <tr key={deal.deal_name}>
                        <td className="growth-deal-name">{deal.deal_name}</td>
                        <td>{deal.organization}</td>
                        <td>{deal.capture_manager}</td>
                        <td>{formatCompactCurrency(deal.tcv)}</td>
                        <td><DealTypeBadge type={deal.deal_type} /></td>
                        <td>{deal.prime_sub}</td>
                        <td>{deal.phase}</td>
                        <td><PwinBadge pwin={deal.pwin} /></td>
                        <td>{deal.expected_rfp || '--'}</td>
                        <td>{deal.expected_award || '--'}</td>
                        <td>{deal.customer}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Row 9 — Won Deals Table (collapsible) */}
      <div className="forecast-chart-section">
        <div className="card">
          <div className="card-header">
            <span className="card-title">Won Deals</span>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', color: 'var(--status-active)' }}>
              {won_deals?.length || 0} awards
            </span>
          </div>
          <button
            className={`forecast-expand-bar${wonTableOpen ? ' expanded' : ''}`}
            onClick={() => setWonTableOpen(!wonTableOpen)}
          >
            <svg className="forecast-expand-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
            View Won Deals
          </button>
          {wonTableOpen && won_deals && (
            <div className="forecast-data-table">
              <div style={{ overflowX: 'auto' }}>
                <table className="growth-deal-table">
                  <thead>
                    <tr>
                      <th>Deal</th>
                      <th>Org</th>
                      <th>TCV</th>
                      <th>Type</th>
                      <th>Prime/Sub</th>
                      <th>Award Date</th>
                      <th>Customer</th>
                      <th>PoP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {won_deals.map((deal) => (
                      <tr key={deal.deal_name}>
                        <td className="growth-deal-name">{deal.deal_name}</td>
                        <td>{deal.organization}</td>
                        <td>{formatCompactCurrency(deal.tcv)}</td>
                        <td><DealTypeBadge type={deal.deal_type} /></td>
                        <td>{deal.prime_sub}</td>
                        <td>{deal.award_date || '--'}</td>
                        <td>{deal.customer}</td>
                        <td>{deal.period_of_performance || '--'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

GrowthDashboard.propTypes = {
  onNavigate: PropTypes.func.isRequired,
  user: PropTypes.shape({
    email: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default GrowthDashboard;
